import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, AlertTriangle, Eye, CalendarPlus, ArrowUpRight, X } from "lucide-react";
import OsobaKolecko from "./OsobaKolecko";
import { FazeDot, etapa, fazeBarva, nazevEtapy, pipelineEtapy } from "../lib/faze";
import TechIkony from "./TechIkony";
import { fmtKcKratce } from "../lib/format";
import { ikonaTypu } from "../lib/typyAktivit";
import { sklon } from "../lib/filtr";
import { useJeMobil, omezenyPohyb } from "../lib/mobil";

/**
 * Tabule obchodních případů (Pipedrive) — jen přehled, bez tažení: fázi případu
 * počítá server z technologií a mění se kroky (zaměření, nabídka, schválení…).
 * Další krok se spustí z ⋯ → Náhled a další krok.
 */
/** Sloupce tabule: fáze s mezikroky firmy a Předáno. */
export const tabuleEtapy = () => [...pipelineEtapy(), "DOKONCENO"];
const CHLADNE_DNI = 14;
const DNY = ["ne", "po", "út", "st", "čt", "pá", "so"];

const den = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
/** „dnes 10:00“, „zítra 9:00“, „pá 16. 10.“ */
export function kdyKrok(iso) {
  const d = new Date(iso);
  const rozdil = Math.round((den(d) - den(new Date())) / 86400000);
  const cas = `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`;
  if (rozdil === 0) return `dnes ${cas}`;
  if (rozdil === 1) return `zítra ${cas}`;
  return `${DNY[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}.`;
}
const poTerminuDni = (iso) => Math.max(1, Math.round((den(new Date()) - den(new Date(iso))) / 86400000));

export const uzavreny = (p) => p.faze === "DOKONCENO" || p.faze === "ZTRACENO";
/** Vázne: bez dalšího kroku, krok po termínu, nebo déle než 14 dní ve fázi. */
export const chladne = (p) => !uzavreny(p) && (!p.dalsi_krok || p.dalsi_krok.po_terminu || (p.dni_ve_fazi || 0) > CHLADNE_DNI);

/** Čip dalšího kroku: naplánováno (zeleně), po termínu (červeně), chybí (oranžově). */
export function KrokCip({ p, maly = false, onOtevri }) {
  if (uzavreny(p)) return null;
  const k = p.dalsi_krok;
  // Úzký sloupec: název a čas se zalomí pod sebe, nic se neusekne.
  const zaklad = `inline-flex items-start gap-1.5 self-start max-w-full text-left rounded-[7px] leading-[1.25] ${maly ? "py-1 px-1.5 text-[11.5px]" : "py-[5px] px-2 text-[12px]"}`;
  if (!k) {
    return (
      <span className={`${zaklad} bg-[#fdf3e2] text-[#a0610c]`} data-testid={`krok-chybi-${p.id}`}>
        <AlertTriangle size={13} className="shrink-0 mt-px" />
        <span className="min-w-0"><b>Chybí další krok</b>{" "}
          <span className="opacity-85 whitespace-nowrap">· {p.dni_ve_fazi} {sklon(p.dni_ve_fazi, ["den", "dny", "dní"])} ve fázi</span></span>
      </span>
    );
  }
  const Ikona = ikonaTypu(k.typ);
  const barva = k.po_terminu ? "bg-[#fdecea] text-danger" : "bg-success-bg text-success";
  const kdy = k.po_terminu ? `${poTerminuDni(k.date_end || k.date_start)} ${sklon(poTerminuDni(k.date_end || k.date_start), ["den", "dny", "dní"])} po termínu`
    : kdyKrok(k.date_start);
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOtevri?.(k.id); }} title={`${k.nazev} · ${kdy}`}
      className={`${zaklad} ${barva} hover:brightness-95`} data-testid={`krok-${p.id}`}>
      <Ikona size={13} className="shrink-0 mt-px" />
      <span className="min-w-0"><b className="break-words">{k.nazev || k.typ}</b>{" "}<span className="opacity-85 whitespace-nowrap">{kdy}</span></span>
    </button>
  );
}

/** Lidé na kartě: obchodník a vedoucí montáže (když je jiný). */
export function Lide({ p, tymMapa, size = 22 }) {
  const ids = [p.obchodnik_id, p.vedouci_montaze_id].filter((x, i, a) => x && a.indexOf(x) === i);
  if (!ids.length) return null;
  return (
    <span className="flex items-center shrink-0">
      {ids.map((id, i) => {
        const m = tymMapa[id] || { id, name: "?" };
        return <OsobaKolecko key={id} id={id} name={m.name} url={m.photo_url} size={size}
          className={`ring-2 ring-white ${i ? "-ml-1.5" : ""}`} />;
      })}
    </span>
  );
}

function TechCipy({ p, slovy }) {
  const ts = (p.technologie || []).filter((t) => t.faze !== "ZTRACENO" || p.faze === "ZTRACENO");
  // Tečka fáze jen když se fáze technologií liší — je vidět, co případ drží v nižší fázi.
  const ruzne = new Set(ts.map(etapa)).size > 1;
  // Na počítači ikony s názvem po najetí myší, na telefonu (bez hoveru) slovy.
  if (!slovy) return <TechIkony technologie={ts} sFazi={ruzne} />;
  return (
    <span className="flex flex-wrap gap-1">
      {ts.map((t) => (
        <span key={t.id} title={ruzne ? `${t.kategorie_name}: ${nazevEtapy(etapa(t))}` : t.kategorie_name}
          className="label-up inline-flex items-center gap-1 bg-inputbg border border-line-strong rounded-chip px-1.5 py-0.5 text-muted">
          {ruzne && <FazeDot faze={etapa(t)} className="h-1.5 w-1.5" />}{t.kategorie_name}
        </span>
      ))}
    </span>
  );
}

export function Karta({ p, money, vl, tymMapa, onDetail, onMenu, onNahled, onKrok, velka = false }) {
  const okraj = p.dalsi_krok?.po_terminu && !uzavreny(p) ? "inset 3px 0 0 #d93a2b"
    : chladne(p) ? "inset 3px 0 0 #f0a020" : undefined;
  return (
    <article tabIndex={0} data-testid={`karta-${p.id}`} onClick={() => onDetail(p)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter") onDetail(p);
        if (e.key === " ") { e.preventDefault(); onNahled(p); }
      }}
      style={{ boxShadow: okraj ? `${okraj}, 0 1px 2px rgba(20,24,33,.04)` : undefined }}
      className="rounded-[12px] bg-white border border-line shadow-card px-3 py-2.5 flex flex-col gap-1 cursor-pointer select-none hover:shadow-[0_6px_18px_rgba(20,24,33,.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2">
      <div className="flex items-start gap-2">
        <span className={`flex-1 min-w-0 font-700 text-ink line-clamp-2 ${velka ? "text-[15.5px]" : "t-telo"}`}>{p.nazev}</span>
        <button type="button" aria-label={`Další akce: ${p.nazev}`} data-testid={`karta-menu-${p.id}`}
          onClick={(e) => { e.stopPropagation(); onMenu(p, e.currentTarget); }}
          className="tap md:min-h-0 md:min-w-0 shrink-0 h-11 w-11 md:h-7 md:w-7 -mr-1 -mt-0.5 rounded-[7px] inline-flex items-center justify-center text-muted hover:text-ink hover:bg-inputbg">
          <MoreHorizontal size={16} />
        </button>
      </div>
      {vl.klient && <span className="t-meta text-sec truncate -mt-1">{p.client_name || "klient chybí"} · {p.cislo}</span>}
      {(money && vl.hodnota) || vl.obchodnik ? (
        <div className="flex items-center gap-2 min-h-[22px]">
          {money && vl.hodnota && (
            <span className={`amount font-800 text-ink flex-1 ${velka ? "text-[15.5px]" : "t-telo"}`} title={`${Math.round(p.hodnota || 0).toLocaleString("cs-CZ")} Kč`}>
              {fmtKcKratce(p.hodnota || 0)}
            </span>
          )}
          {vl.obchodnik && <span className="ml-auto"><Lide p={p} tymMapa={tymMapa} /></span>}
        </div>
      ) : null}
      {vl.technologie && <TechCipy p={p} slovy={velka} />}
      {vl.krok && <KrokCip p={p} onOtevri={onKrok} />}
      {vl.dni && !uzavreny(p) && !(vl.krok && !p.dalsi_krok) && (
        <span className="t-meta text-muted">{p.dni_ve_fazi} {sklon(p.dni_ve_fazi, ["den", "dny", "dní"])} ve fázi</span>
      )}
    </article>
  );
}

/** Menu ⋯ na kartě: náhled s dalším krokem, naplánovat krok, detail. */
export function KartaMenu({ p, kotva, onClose, onNahled, onNaplanovat, onDetail, smiPlanovat }) {
  const mobil = useJeMobil();
  const ref = useRef(null);
  const [poloha, setPoloha] = useState(null);
  useLayoutEffect(() => {
    if (mobil || !kotva) return;
    const r = kotva.getBoundingClientRect();
    const dole = window.innerHeight - r.bottom;
    setPoloha({ left: Math.max(8, r.right - 220), top: dole > 170 ? r.bottom + 4 : undefined, bottom: dole > 170 ? undefined : window.innerHeight - r.top + 4 });
  }, [mobil, kotva]);
  useEffect(() => {
    const klik = (e) => { if (!ref.current?.contains(e.target) && !kotva?.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === "Escape") { onClose(); kotva?.focus(); } };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", klik); document.removeEventListener("keydown", esc); };
  }, [kotva, onClose]);
  useLayoutEffect(() => { ref.current?.querySelector("button")?.focus(); }, [poloha, mobil]);

  const polozky = [
    { ikona: Eye, text: "Náhled a další krok", fn: onNahled, testid: "menu-nahled" },
    ...(smiPlanovat ? [{ ikona: CalendarPlus, text: "Naplánovat další krok", fn: onNaplanovat, testid: "menu-naplanovat" }] : []),
    { ikona: ArrowUpRight, text: "Otevřít detail", fn: onDetail, testid: "menu-detail" },
  ];
  const seznam = polozky.map((x) => (
    <button key={x.text} type="button" onClick={() => { onClose(); x.fn(p); }} data-testid={x.testid}
      className="vyber-polozka w-full min-h-[56px] md:min-h-[36px] px-3 md:px-2.5 rounded-[8px] flex items-center gap-2.5 text-left t-telo md:text-[13px] text-ink">
      <x.ikona size={16} className="text-muted shrink-0" />{x.text}
    </button>
  ));
  if (mobil) {
    return createPortal(
      <div className="fixed inset-0 z-[80] flex items-end">
        <div className="absolute inset-0 bg-black/30 zaclon-in" onClick={onClose} />
        <div ref={ref} role="dialog" aria-modal="true" aria-label={p.nazev}
          className="karta-panel relative bg-white w-full rounded-t-sheet shadow-panel p-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)" }}>
          <div className="flex items-center px-3 py-2">
            <span className="t-sekce font-700 text-ink flex-1 truncate">{p.nazev}</span>
            <button type="button" onClick={onClose} aria-label="Zavřít" className="tap -mr-2 text-muted"><X size={20} /></button>
          </div>
          {seznam}
        </div>
      </div>, document.body);
  }
  if (!poloha) return null;
  return createPortal(
    <div ref={ref} role="menu" className={`${omezenyPohyb() ? "" : "vyber-menu"} fixed z-[200] w-[220px] bg-white rounded-[12px] border border-line-softer shadow-panel p-1.5`}
      style={{ left: poloha.left, top: poloha.top, bottom: poloha.bottom }} data-testid="karta-menu">
      {seznam}
    </div>, document.body);
}

function Hlavicka({ faze, i, celkem, polozky, money }) {
  const suma = polozky.reduce((a, p) => a + (p.hodnota || 0), 0);
  const predano = faze === "DOKONCENO";
  const ztraceno = faze === "ZTRACENO";
  const podil = Math.round(((i + 1) / celkem) * 100);
  return (
    <div className="rounded-[11px] bg-white px-[11px] pt-2 pb-3 relative shrink-0">
      <div className="flex items-center gap-1.5"><FazeDot faze={faze} /><span className="t-sekce font-700 text-ink truncate">{ztraceno ? "Ztracené" : nazevEtapy(faze)}</span></div>
      <div className="t-meta text-sec amount mt-0.5" title={money ? `${Math.round(suma).toLocaleString("cs-CZ")} Kč` : undefined}>
        {money && !ztraceno ? `${fmtKcKratce(suma)} · ` : ""}{polozky.length}
      </div>
      <span aria-hidden="true" className="absolute left-[11px] right-[11px] bottom-[6px] h-[3px] rounded-full"
        style={{ background: ztraceno ? "#eeece7" : predano ? "#17876a" : `linear-gradient(90deg, #ff6a13 ${podil}%, #eeece7 ${podil}%)` }} />
    </div>
  );
}

/** Tabule na počítači a tabletu: sloupce fází, na tabletu se posouvají do strany. */
export function Tabule({ polozky, ztracene, money, vl, tymMapa, onDetail, onMenu, onNahled, onKrok, canWrite, onNovy }) {
  const faze = useMemo(() => (ztracene ? [...tabuleEtapy(), "ZTRACENO"] : tabuleEtapy()), [ztracene]);
  const ref = useRef(null);
  const [vyska, setVyska] = useState(null);
  useLayoutEffect(() => {
    const zmer = () => { const r = ref.current?.getBoundingClientRect(); if (r) setVyska(Math.max(420, window.innerHeight - r.top - 16)); };
    zmer();
    window.addEventListener("resize", zmer);
    return () => window.removeEventListener("resize", zmer);
  }, []);
  const podle = useMemo(() => Object.fromEntries(faze.map((f) => [f, polozky.filter((p) => etapa(p) === f)])), [faze, polozky]);
  return (
    <div ref={ref} className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-1 [scroll-snap-type:x_proximity]" data-testid="obchod-tabule"
      style={{ height: vyska || undefined }}>
      <div className="h-full grid gap-[10px] grid-cols-[repeat(var(--sloupcu),260px)] lg:grid-cols-[repeat(var(--sloupcu),minmax(170px,1fr))]"
        style={{ "--sloupcu": faze.length }}>
        {faze.map((f, i) => (
          <section key={f} aria-label={nazevEtapy(f)} data-testid={`sloupec-${f}`}
            className="bg-inputbg border border-line rounded-card p-[7px] flex flex-col min-h-0 [scroll-snap-align:start]">
            <Hlavicka faze={f} i={i} celkem={tabuleEtapy().length} polozky={podle[f]} money={money} />
            <div className="flex-1 min-h-[80px] flex flex-col gap-2 pt-2 overflow-y-auto [scrollbar-width:none]">
              {podle[f].map((p) => <Karta key={p.id} p={p} money={money} vl={vl} tymMapa={tymMapa}
                onDetail={onDetail} onMenu={onMenu} onNahled={onNahled} onKrok={onKrok} />)}
              {!podle[f].length && <div className="t-meta text-muted text-center py-6">Tady nic není</div>}
              {f === "NOVY" && canWrite && (
                <button type="button" onClick={onNovy} className="tap md:min-h-0 mt-auto h-9 rounded-[10px] t-meta font-700 text-sec hover:text-ink hover:bg-white inline-flex items-center justify-center gap-1"
                  data-testid="tabule-novy">+ Nový případ</button>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/** Telefon: šipkový pás fází nahoře, pod ním karty jedné fáze. */
export function TabuleMobil({ polozky, ztracene, money, vl, tymMapa, faze, setFaze, onDetail, onMenu, onNahled, onKrok }) {
  const seznam = ztracene ? [...tabuleEtapy(), "ZTRACENO"] : tabuleEtapy();
  const pas = useRef(null);
  useEffect(() => {
    pas.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest", inline: "center", behavior: omezenyPohyb() ? "auto" : "smooth" });
  }, [faze]);
  const vybrane = polozky.filter((p) => etapa(p) === faze);
  const suma = vybrane.reduce((a, p) => a + (p.hodnota || 0), 0);
  const poTerminu = vybrane.filter((p) => p.dalsi_krok?.po_terminu && !uzavreny(p)).length;
  return (
    <div data-testid="obchod-tabule-mobil">
      <div ref={pas} className="flex overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none]" role="tablist" aria-label="Fáze">
        {seznam.map((f, i) => {
          const v = polozky.filter((p) => etapa(p) === f);
          const akt = f === faze;
          const barva = akt ? (f === "DOKONCENO" ? "#17876a" : f === "ZTRACENO" ? "#d93a2b" : "#ff6a13") : "#ffffff";
          return (
            <button key={f} type="button" role="tab" aria-current={akt ? "true" : undefined} aria-selected={akt} onClick={() => setFaze(f)}
              data-testid={`pas-${f}`}
              className={`shrink-0 h-[54px] min-w-[112px] -mr-2 pl-6 pr-6 flex flex-col justify-center text-left ${i === 0 ? "rounded-l-[12px] pl-4" : ""}`}
              style={{ background: barva, color: akt ? "#fff" : fazeBarva(f).col,
                clipPath: i === 0 ? "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)"
                  : "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)",
                filter: akt ? undefined : "drop-shadow(0 0 0.5px #e0deda)" }}>
              <span className="font-700 text-[14.5px] leading-tight whitespace-nowrap">{f === "ZTRACENO" ? "Ztracené" : nazevEtapy(f)}</span>
              <span className={`text-[12px] amount whitespace-nowrap ${akt ? "text-white/85" : "text-sec"}`}>
                {v.length}{money && f !== "ZTRACENO" ? ` · ${fmtKcKratce(v.reduce((a, p) => a + (p.hodnota || 0), 0))}` : ""}
              </span>
            </button>
          );
        })}
      </div>
      <div className="t-meta text-sec my-3">
        {vybrane.length} {sklon(vybrane.length, ["případ", "případy", "případů"])}{money ? ` · ${fmtKcKratce(suma)}` : ""}
        {poTerminu > 0 && <span className="text-danger"> · {poTerminu} po termínu</span>}
      </div>
      <div className="flex flex-col gap-2">
        {vybrane.map((p) => <Karta key={p.id} p={p} money={money} vl={vl} tymMapa={tymMapa} velka
          onDetail={onDetail} onMenu={onMenu} onNahled={onNahled} onKrok={onKrok} />)}
        {!vybrane.length && <div className="card t-meta text-muted text-center py-8">Ve fázi {nazevEtapy(faze)} nic není.</div>}
      </div>
    </div>
  );
}
